import React from 'react';
import styled, { css } from 'styled-components/macro';
import Breakpoints from 'themes/constants/breakpoints';
import { IEachIssue, IProfileBookmarksIssuesItem, IProfileBookmarksPagesItem } from 'constants/types';
import get from 'lodash.get';
import placeholder from 'media/images/placeholder.jpg';
import { useHistory, useRouteMatch } from 'react-router-dom';
import RoutePath from 'routes/constants/routes';
import { ReactComponent as XIconSVG } from 'media/icons/x.svg';
import { APILinks } from 'constants/api';
import moment from 'moment';
import useFetchToggle from 'hooks/useFetchToggle';
import useTranslate from 'hooks/useTranslate';
import LazyImg from 'components/Lazy/img/LazyImg';
import * as DEFAULTS from 'constants/defaults';
import { ReactComponent as FreeIconSVG } from 'media/icons/free-icon.svg';

// Styles
const Wrap = styled.div<{ isSlider: boolean, disabled?: boolean, isJournal?: boolean, isIssue?: boolean }>`
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    min-width: 126px;
    min-height: ${({ isJournal, isSlider }) => ((isSlider && isJournal) ? '285px' : '210px')};
    width: ${({ isSlider }) => isSlider && '60vw'};
    height: ${({ isJournal, isSlider }) => (isJournal && isSlider ? '85vw' : 'unset')};
    background: ${({ theme }) => theme.background.card.journal};
    border-radius: ${({ theme }) => theme.borderRadius.card.journal};
    box-shadow: ${({ theme }) => theme.boxShadow.card.journal};
    color: ${({ theme }) => theme.palette.black};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    overflow: hidden;
    opacity: ${({ disabled }) => disabled && 0.3};
    @media only screen and (${Breakpoints.MOBILE_L}) {
        height: unset;
    }
    @media only screen and (${Breakpoints.TABLET_ES}) {
        height: unset;
    }
    @media only screen and (${Breakpoints.TABLET_S}) {
        height: ${({ isSlider }) => isSlider && 'unset'};
    }
    @media only screen and (${Breakpoints.TABLET}) {
        height: ${({ isSlider }) => isSlider && 'unset'};
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        width: ${({ isSlider }) => isSlider && '280px'};
        height: ${({ isSlider }) => isSlider && 'unset'};
    }
`;
const Img = styled.div`
    position: relative;
    height: 0;
    padding-bottom: 125%;
    & > img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${({ theme }) => theme.background.skeleton};
        object-fit: cover;
    }
    @media only screen and (${Breakpoints.TABLET}) {
        padding-bottom: 140%;
    }
`;
const FreeIconWrap = styled(FreeIconSVG)`
    position: absolute;
    left: 10px;
    bottom: 10px;
    height: 13%;
    width: auto;
`;
const RightTextShadow = css<{ hasControl?: boolean }>`
    position: relative;
    overflow: hidden;
    &:after {
        content: "";
        position: absolute;
        top: 0;
        right: ${({ hasControl }) => (hasControl ? '40px' : '0')};
        bottom: 0;
        width: 0;
        box-shadow: ${({ theme }) => `0 0 10px 10px ${theme.palette.white}`};
    }
`;
const Name = styled.div<{ hasControl?: boolean, isJournal?: boolean }>`
    position: relative;
    display: flex;
    flex-flow: ${({ isJournal }) => (isJournal ? 'column nowrap' : 'row nowrap')};
    align-items: center;
    justify-content: flex-start;
    flex-grow: 1;
    font-size: 12px;
    line-height: 15px;
    min-height:
    color: ${({ theme }) => theme.palette.greyDark};
    font-weight: 700;
    height: 100%;
    padding: ${({ hasControl }) => (hasControl ? '7px 30px 7px 10px' : '7px 10px')};
    & > span:first-letter {
        text-transform: uppercase;
    }
    ${() => RightTextShadow};
    @media only screen and (${Breakpoints.LAPTOP}) {
        font-size: 18px;
        font-weight: 300;
        padding: ${({ hasControl }) => (hasControl ? '10px 30px 10px 10px' : '10px 15px')};
    }
`;
const JournalName = styled.div<{ hasControl?: boolean }>`
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    height: 100%;
    justify-content: center;
    padding: ${({ hasControl }) => (hasControl ? '7px 30px 7px 10px' : '6px 10px')};
    @media only screen and (${Breakpoints.LAPTOP}) {
        padding: ${({ hasControl }) => (hasControl ? '10px 30px 10px 10px' : '8px 15px')};
    }
`;
const JournalNameTitle = styled.div`
    font-size: 18px;
    line-height: 20px;
    font-weight: 700;
    white-space: nowrap;
    margin-bottom: 6px;
    ${() => RightTextShadow};
    @media only screen and (${Breakpoints.LAPTOP}) {
        font-size: 18px;
        font-weight: 300;
        margin-bottom: 3px;
    }
`;
const JournalNameDate = styled.div`
    font-size: 12px;
    font-weight: 300;
    color: ${({ theme }) => theme.palette.greyDark};
    white-space: nowrap;
    ${() => RightTextShadow};
    @media only screen and (${Breakpoints.LAPTOP}) {
        font-size: 14px;
        font-weight: 300;
    }
`;
const ControlDelete = styled.div<{ disabled?: boolean }>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 100%;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    & > span {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: ${({ theme }) => theme.palette.secondary};
        width: 16px;
        height: 16px;
        svg {
            color: ${({ theme }) => theme.palette.white};
            width: 6px;
            height: 6px;
            stroke-width: 5px;
        }
    }
`;
const DeletedCover = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-radius: inherit;
    background: ${({ theme }) => theme.background.skeleton};
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    padding: 10px;
    opacity: 0.7;
    z-index: 100;
`;

// Interfaces
interface IJournalsCard {
  item: IEachIssue | IProfileBookmarksPagesItem | IProfileBookmarksIssuesItem
  isSlider?: boolean
  className?: string
  type?: JournalsCardType
  journalName?: string
}
export interface IMatchParams {
  categoryId: string;
}
export enum JournalsCardType {
  JOURNAL = 'JOURNAL',
  JOURNAL_WITH_CONTROL_FAVORITES = 'JOURNAL_WITH_CONTROL_FAVORITES',
  JOURNAL_WITH_CONTROL_BOOKMARKS = 'JOURNAL_WITH_CONTROL_BOOKMARKS',
  ISSUE = 'ISSUE',
  ISSUE_WITH_CONTROL_FAVORITES = 'ISSUE_WITH_CONTROL_FAVORITES',
  ISSUE_WITH_CONTROL_BOOKMARKS = 'ISSUE_WITH_CONTROL_BOOKMARKS',
  PAGE = 'PAGE',
  PAGE_WITH_CONTROL_BOOKMARKS = 'PAGE_WITH_CONTROL_BOOKMARKS',
}

// Component
const JournalsCard = (props: IJournalsCard) => {
  const {
    item,
    type = JournalsCardType.JOURNAL,
    isSlider = false,
    className = '',
    journalName = '',
  } = props;

  const history = useHistory();
  const match = useRouteMatch<IMatchParams>();
  const categoryId = Number(match.params.categoryId) || 0;

  const [getText] = useTranslate();

  const freeIssue: boolean = get(item, ['freeIssue'], false);

  const isJournalWithControlFavorites = type === JournalsCardType.JOURNAL_WITH_CONTROL_FAVORITES;
  const isJournalWithControlBookmarks = type === JournalsCardType.JOURNAL_WITH_CONTROL_BOOKMARKS;
  const isIssueWithControlFavorites = type === JournalsCardType.ISSUE_WITH_CONTROL_FAVORITES;
  const isIssueWithControlBookmarks = type === JournalsCardType.ISSUE_WITH_CONTROL_BOOKMARKS;
  const isPageWithControlBookmarks = type === JournalsCardType.PAGE_WITH_CONTROL_BOOKMARKS;

  const isJournal = type === JournalsCardType.JOURNAL
        || isJournalWithControlFavorites
        || isJournalWithControlBookmarks;

  const isIssue = type === JournalsCardType.ISSUE
        || isIssueWithControlFavorites
        || isIssueWithControlBookmarks;

  const isPage = type === JournalsCardType.PAGE
        || isPageWithControlBookmarks;

  const releaseDate = get(item, ['releaseDate'], '');
  const journalId = get(item, ['journalId'], 0);
  const issueId: number = isPage ? get(item, ['issueId'], 0) : get(item, ['id'], 0);
  const pageId: number = get(item, ['pageId'], 0);
  const pageNumber: number = get(item, ['page'], 0);
  const page: string = get(item, ['page'], DEFAULTS.PROJECT_TITLE);
  const nameValue: string = get(item, ['name'], DEFAULTS.PROJECT_TITLE);
  const coverMobile = isPage
    ? `/api/profile/bookmark/issue-page/${pageId}.jpg`
    : get(item, ['coverMobile'], placeholder);
  const name = isPage
    ? `${page} ${getText(['words', 'page'])}`
    : isIssue
      ? `#${nameValue}`
      : moment(releaseDate).format('MMM YYYY г.');

  const hasControlFavorites = isIssueWithControlFavorites || isJournalWithControlFavorites;
  const hasControlBookmarks = isIssueWithControlBookmarks
        || isJournalWithControlBookmarks
        || isPageWithControlBookmarks;

  const hasControl = hasControlFavorites || hasControlBookmarks;

  const id = isIssue ? issueId : isJournal ? journalId : pageId;

  const {
    isHas,
    isLoading: isHasLoading,
    handleToggle: handleToggleDeleteHas,
  } = useFetchToggle({
    apiLink: isPage ? APILinks.BOOKMARKS_ISSUES_PAGE_TOGGLE : APILinks.FAVORITE_JOURNALS,
    initialValue: true,
    initialLoading: false,
    id,
    keyId: isPage ? 'issuePageId' : 'id',
  });

  const isDeleted = !isHas;

  const journalLink = `${RoutePath.JOURNALS_PATH}/${categoryId}/detail/${journalId}`;
  const issueLink = `${RoutePath.JOURNALS_PATH}/${categoryId}/detail/${journalId}/issues/${issueId}`;
  const issuePageLink = `${issueLink}/bookmarks?page=${pageNumber}`;
  const redirectLink = isIssue ? issueLink : isJournal ? journalLink : issuePageLink;
  const handleRedirect = () => {
    if (!isHasLoading) {
      history.push(redirectLink);
    }
  };

  const handleToggleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleToggleDeleteHas();
  };

  const controlDelete = (
    <ControlDelete
      disabled={isHasLoading}
      onClick={handleToggleDelete}
    >
      <span><XIconSVG /></span>
    </ControlDelete>
  );

  return (
    <Wrap
      className={className}
      onClick={handleRedirect}
      isSlider={isSlider}
      disabled={isHasLoading}
      isJournal={isJournal}
      isIssue={isIssue}
    >
      <Img>
        <LazyImg
          src={coverMobile}
          alt={journalName}
          decoding="async"
          onError={(e: any) => {
            e.target.onerror = null;
            e.target.src = placeholder;
          }}
        />
        {freeIssue && isIssue && <FreeIconWrap />}
      </Img>
      {isJournal ? (
        <JournalName hasControl={hasControl}>
          <JournalNameTitle>{journalName}</JournalNameTitle>
          <JournalNameDate>{`#${nameValue}`}</JournalNameDate>
          {hasControl
            ? controlDelete
            : null}
        </JournalName>
      ) : (
        <Name hasControl={hasControl}>
          <span>{name}</span>
          {hasControl
            ? controlDelete
            : null}
        </Name>
      )}
      {isDeleted && !isHasLoading ? (
        <DeletedCover
          onClick={handleToggleDelete}
        >
          {getText(['words', 'hasDeletedWithUndo'])}
        </DeletedCover>
      ) : null}
    </Wrap>
  );
};

JournalsCard.defaultProps = {
  isSlider: false,
  className: '',
  type: JournalsCardType.JOURNAL,
  journalName: '',
};

export default JournalsCard;
